/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Input, Popconfirm, Skeleton, Space, Table, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import BotAvatar from '../UI/BotAvatar';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import TableCellTypography from '../UI/TableCellTypography';
import PredefinedEntityModal from '../Modals/PredefinedEntityModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { useFetchOrganizationPredefinedResponsesQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDeletePredefinedResponseMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

const LiveChatCannedResponsesSettingsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { handleMouseDown, isDragging } = useDragDetection();

  const [isModalOpen, setIsModalOpen] = useState();
  const [mode, setMode] = useState();
  const [selectedItem, setSelectedItem] = useState();
  const [searchFilter, setSearchFilter] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: organizationPredefinedResponses, isFetching: isFetchingPredefinedResponses } = useFetchOrganizationPredefinedResponsesQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [deleteOrganizationPredefinedResponse] = useDeletePredefinedResponseMutation();

  const onEditClick = (record) => {
    setMode('responses');
    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  return (
    <Fragment>
      <Space
        size={16}
        align="center"
        className="justify-content-between mb-4 w-100"
        wrap
      >
        <Input
          size="large"
          placeholder={t('Search in contents')}
          allowClear
          disabled={isFetchingPredefinedResponses}
          prefix={<SearchOutlined className="text-muted" />}
          onChange={(e) => setSearchFilter(e.target.value)}
        />

        <Button
          key="addResponse"
          type="primary"
          icon={<PlusIcon />}
          className="d-block ml-auto"
          size="large"
          onClick={() => {
            setMode('responses');
            setSelectedItem();
            setIsModalOpen(true);
          }}
        >
          {t('Add Response')}
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(!isDragging()) {
              onEditClick(record);
            }
          }
        })}
        rowClassName="cursor-pointer"
        loading={!organizationPredefinedResponses || (isFetchingBots && !bots)}
        dataSource={!searchFilter ? organizationPredefinedResponses : organizationPredefinedResponses?.filter(i => i.name.toLowerCase().includes(searchFilter.toLowerCase()) || i.text?.toLowerCase().includes(searchFilter.toLowerCase()))}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          size: 'default',
          showTotal: (total) => `${t('Total Responses')}: ${total}`
        }}
        onChange={handleTableChange}
        columns={[
          {
            key: 'buttonTitle',
            dataIndex: 'name',
            title: t('Button Title'),
            sorter: (a, b) => a.name.localeCompare(b.name),
            ellipsis: true,
            render: (_text) => (
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchFilter]}
                autoEscape
                textToHighlight={(_text)}
              />
            )
          },
          {
            title: t('Categories'),
            dataIndex: 'categories',
            key: 'categories',
            render: (_text, record) => {
              if (!record.categories || isEmptyArray(record.categories)) {
                return '-';
              }

              return (
                <ResponsiveTagsList
                  values={record.categories.map((category, index) => ({ label: category, value: index }))}
                  mode="tags"
                  className="w-100"
                  tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
                />
              );
            },
            filters: organizationPredefinedResponses ? organizationPredefinedResponses.reduce((acc, curr) => {
              if (!curr.categories || isEmptyArray(curr.categories)) {
                return acc;
              }
              return acc
                .concat(curr.categories.map((category) => ({ text: category, value: category }))) // Flatten the array
                .filter((item, index, self) => self.findIndex((i) => i.value === item.value) === index); // Remove duplicates
            }
            , []) : [],
            onFilter: (value, record) => {
              if (!record.categories || isEmptyArray(record.categories)) {
                return false;
              }
              return record.categories.includes(value);
            },
            filterIcon: useTableFilterIcon
          },
          {
            key: 'message',
            dataIndex: 'text',
            title: t('Message'),
            ellipsis: { showTitle: false },
            render: (value) => (
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchFilter]}
                autoEscape
                textToHighlight={(value)}
              >
                <TableCellTypography>{value}</TableCellTypography>
              </Highlighter>
            )
          },
          {
            key: 'visibility',
            dataIndex: 'organizationWide',
            title: t('Visibility'),
            responsive: ['md'],
            filters: [
              {
                text: 'All Workspace users',
                value: true
              },
              {
                text: 'Owner',
                value: false
              }
            ],
            onFilter: (value, record) => record.organizationWide === value,
            filterIcon: useTableFilterIcon,
            render: (value) => (
              value ? t('All Workspace users') : t('Owner')
            )
          },
          {
            key: 'bots',
            title: t('Agent Availability'),
            responsive: ['sm'],
            dataIndex: 'tenantIds',
            ellipsis: true,
            filterMultiple: true,
            filterSearch: (input, record) => {
              const botDisplayName = bots.items.find((bot) => bot.id === record.value).displayName.toLowerCase();
              return botDisplayName.includes(input.toLowerCase());
            },
            filters: bots?.items.map(bot => ({
              text: (
                <Space size={4} align="center" wrap={false}>
                  <BotAvatar bot={bot} />
                </Space>
              ),
              value: bot.id
            })),
            onFilter: (value, record) => {
              return record?.tenantIds?.includes(value);
            },
            filterIcon: useTableFilterIcon,
            render: record => {
              if (!record && isFetchingPredefinedResponses) {
                return <Skeleton.Button active size="small" />;
              }

              if (!record?.length > 0) {
                return t('All Workspace Agents');
              }

              const associatedBots = bots?.items?.filter(bot => record.includes(bot.id)) ?? [];

              return (
                <ResponsiveTagsList
                  values={
                    associatedBots.map((bot) => ({
                      icon: <BotAvatar avatarSize={24} bot={bot} showBotName={false} />,
                      label: bot.displayName,
                      value: bot.id
                    }))
                  }
                  maxTagCount={4}
                  tagRender={(tag) => {
                    const matchedTag = associatedBots.find(bot => bot.id === tag.value);
                    return (
                      <Tooltip overlayClassName="pointer-events-none" title={matchedTag.displayName}>
                        <BotAvatar avatarSize={24} bot={matchedTag} showBotName={false} className="mr-2" />
                      </Tooltip>
                    );
                  }}
                />
              );
            }
          },
          {
            key: 'actions',
            title: t('Actions'),
            align: 'center',
            width: 100,
            render: (_value, record) => (
              <Space wrap={false}>
                <Tooltip overlayClassName="pointer-events-none" title={t('Edit')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<EditIcon />}
                    onClick={() => onEditClick(record)}
                  />
                </Tooltip>

                <Popconfirm
                  trigger="click"
                  title={t('Delete response?')}
                  placement="topRight"
                  onConfirm={() => {
                    deleteOrganizationPredefinedResponse({ orgId, predefinedResponseId: record.id });
                  }}
                  okText={t('Delete')}
                >
                  <Tooltip overlayClassName="pointer-events-none" title={t('Delete')}>
                    <Button
                      shape="circle"
                      type="text"
                      icon={<DeleteIcon />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            )
          }
        ]}
      />

      <PredefinedEntityModal
        orgLevelForm
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        open={isModalOpen}
        setIsOpen={setIsModalOpen}
        mode={mode}
      />
    </Fragment>
  );
};

export default LiveChatCannedResponsesSettingsTable;
